/**
 * **External clinicians** (5.2), under Who is working: who is on the list, the
 * days each of them is scheduled over the next fortnight, and when each last
 * worked — most recent first, so the people the branch actually uses are at the
 * top and the ones drifting out of reach fall to the bottom.
 *
 * Reworked on Pete's instruction, 2026-09-20: *"relabel the Per Diem section
 * 'External Clinicians', drop the title subtext, drop the 'going cold' badge,
 * sort by last worked descending, remove last contact, change the declared
 * column to 'Scheduled', make the colors in there match the color scheme used in
 * the top section, change the empty text to just 'None'."* The **Going cold**
 * badge and the last-contact column are gone, and since the 30-minute script so
 * is the going-cold fact behind them.
 *
 * No outreach button: bringing someone in is the branch's decision and its own
 * channel (B9). The scheduled days are the same days the coverage drawer reads
 * when it puts an external in "External, availability declared".
 */
import { useMemo } from "react";
import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { dateKeyStart, formatInBranchZone } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { perDiemRoster, type PerDiemRosterRow } from "../../engine/roster";
import { useDemoState } from "../../store/useDemoStore";

export function PerDiemRoster() {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const today = state.meta.demoToday;
  // Most recently worked first; anybody who has never worked here sorts last
  // rather than to the top on an empty string.
  const rows = useMemo(
    () => [...perDiemRoster(state, today)].sort((a, b) => (b.lastWorkedOn ?? "").localeCompare(a.lastWorkedOn ?? "")),
    [state, today],
  );
  if (!rows.length) return null;

  const day = (key: string) => formatInBranchZone(dateKeyStart(key, tz), tz, "EEE MMM d");

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack direction="row" spacing={2} alignItems="baseline" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          External clinicians
        </Typography>
      </Stack>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 190 }}>Clinician</TableCell>
              <TableCell sx={{ width: 110 }}>Discipline</TableCell>
              <TableCell>Scheduled</TableCell>
              <TableCell sx={{ width: 140 }}>Last worked</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.handle} data-handle={r.handle}>
                <TableCell>
                  <Typography variant="body2" component="span">
                    {r.displayName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" component="span">
                    {r.discipline}
                  </Typography>
                </TableCell>
                <TableCell>
                  <DeclaredDays row={r} tz={tz} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={r.lastWorkedOn ? undefined : "text.secondary"}>
                    {r.lastWorkedOn ? day(r.lastWorkedOn) : "—"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function DeclaredDays({ row, tz }: { row: PerDiemRosterRow; tz: string }) {
  if (!row.declaredDays.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        None
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {row.declaredDays.map((d) => (
        <Tooltip key={d} title={formatInBranchZone(dateKeyStart(d, tz), tz, "EEEE MMM d")}>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 0.5,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              // Every chip here is a day this person **is** working, so every chip
              // is the grid's working blue — weekend included. The grid's weekend
              // grey belongs to *empty* cells; reading it onto a scheduled
              // Saturday said the opposite of what the chip is for (Pete,
              // 2026-09-20: *"Jordan Miles weekend shifts are showing gray
              // instead of blue"*).
              bgcolor: "#d7e7f5",
              fontSize: 11,
              whiteSpace: "nowrap",
            }}
          >
            {formatInBranchZone(dateKeyStart(d, tz), tz, "EEE d")}
          </Box>
        </Tooltip>
      ))}
    </Stack>
  );
}
